/**
 * Server clock offset estimation (spec §3.1: views carry `serverNow` and absolute deadlines).
 *
 * Each `state` message gives one sample `offset = view.serverNow - Date.now()` (it underestimates
 * the true offset by the one-way latency, which is fine for countdowns). The estimate is the
 * median of the last `window` accepted samples. Once the estimate is established, samples that
 * deviate more than `outlierMs` from it (a slow, delayed message) are ignored; if `resetAfter`
 * outliers in a row agree with each other the clock really jumped and we start over from them.
 */
export interface ClockOptions {
  window?: number;
  outlierMs?: number;
  resetAfter?: number;
}

export class ClockOffset {
  private samples: number[] = [];
  private outliers: number[] = [];
  private current = 0;
  private readonly window: number;
  private readonly outlierMs: number;
  private readonly resetAfter: number;

  constructor(opts: ClockOptions = {}) {
    this.window = opts.window ?? 5;
    this.outlierMs = opts.outlierMs ?? 1500;
    this.resetAfter = opts.resetAfter ?? 3;
  }

  /** Smoothed offset in ms (server − local). 0 until the first sample. */
  get value(): number {
    return this.current;
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  /** Add a sample; returns the new smoothed offset. */
  sample(serverNow: number, localNow: number): number {
    const s = serverNow - localNow;
    if (!Number.isFinite(s)) return this.current;
    const established = this.samples.length >= Math.min(3, this.window);
    if (established && Math.abs(s - this.current) > this.outlierMs) {
      this.outliers.push(s);
      if (this.outliers.length >= this.resetAfter && spread(this.outliers) <= this.outlierMs) {
        this.samples = this.outliers.slice(-this.window);
        this.outliers = [];
        this.current = median(this.samples);
      }
      return this.current;
    }
    this.outliers = [];
    this.samples.push(s);
    if (this.samples.length > this.window) this.samples.shift();
    this.current = median(this.samples);
    return this.current;
  }

  reset(): void {
    this.samples = [];
    this.outliers = [];
    this.current = 0;
  }
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1
    ? (sorted[mid] as number)
    : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

function spread(values: readonly number[]): number {
  return Math.max(...values) - Math.min(...values);
}
